import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cloud, Server, Shield, Zap, Database, Cpu } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { setPageMeta } from '../lib/setPageMeta';
const providerIcons = [Cloud, Database, Shield, Server, Cpu];
export function Launch() {
  const {
    t
  } = useLanguage();
  const providers = t('launchPage.providers') as any[];
  useEffect(() => {
    setPageMeta({
      title: 'How to Launch',
      description: 'The backstage guide to launching a platform like Dance for Fans — hosting, infrastructure, security, and scale.'
    });
  }, []);
  return <div className="min-h-screen font-sans" style={{
    backgroundColor: '#1a0408',
    color: '#f5e6d3'
  }} data-id="element-464">
      {/* Cabaret ambient lighting */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" data-id="element-465">
        <motion.div className="absolute top-[-20%] left-[10%] w-[700px] h-[700px] rounded-full blur-3xl" style={{
        backgroundColor: 'rgba(220, 38, 38, 0.15)'
      }} animate={{
        scale: [1, 1.15, 1],
        x: [0, 40, 0]
      }} transition={{
        duration: 14,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-466" />
        <motion.div className="absolute bottom-[-20%] right-[5%] w-[600px] h-[600px] rounded-full blur-3xl" style={{
        backgroundColor: 'rgba(190, 24, 93, 0.18)'
      }} animate={{
        scale: [1, 1.2, 1],
        y: [0, -50, 0]
      }} transition={{
        duration: 16,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-467" />
      </div>

      <div className="relative z-10" data-id="element-468">
        {/* Top bar */}
        <header className="px-6 py-6 border-b" style={{
        borderColor: 'rgba(245, 230, 211, 0.1)'
      }} data-id="element-469">
          <div className="max-w-5xl mx-auto flex items-center justify-between" data-id="element-470">
            <Link to="/" className="inline-flex items-center gap-2 text-sm hover:opacity-80 transition-opacity" style={{
            color: '#f5e6d3'
          }} data-id="element-471">
              <ArrowLeft className="w-4 h-4" data-id="element-472" />
              {t('common.backToMain')}
            </Link>
            <span className="text-xs uppercase tracking-[0.3em]" style={{
            color: '#d4a574'
          }} data-id="element-473">
              {t('launchPage.headerTag')}
            </span>
          </div>
        </header>

        {/* Hero */}
        <section className="px-6 py-24 md:py-32" data-id="element-474">
          <div className="max-w-4xl mx-auto text-center" data-id="element-475">
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-sm uppercase tracking-[0.4em] mb-6" style={{
            color: '#d4a574'
          }} data-id="element-476">
              {t('launchPage.heroTag')}
            </motion.p>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-serif italic text-5xl md:text-7xl lg:text-8xl leading-[1.05] mb-8" style={{
            color: '#f5e6d3'
          }} data-id="element-477">
              {t('launchPage.heroTitleStart')}
              <span style={{
              color: '#dc2626'
            }} data-id="element-478">
                {t('launchPage.heroTitleHighlight')}
              </span>
              {t('launchPage.heroTitleEnd')}
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed" style={{
            color: 'rgba(245, 230, 211, 0.7)'
          }} data-id="element-479">
              {t('launchPage.heroDesc')}
            </motion.p>
          </div>
        </section>

        {/* Intro paragraph */}
        <section className="px-6 pb-16" data-id="element-480">
          <div className="max-w-3xl mx-auto" data-id="element-481">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="border-l-2 pl-8 py-4" style={{
            borderColor: '#dc2626'
          }} data-id="element-482">
              <p className="font-serif text-2xl md:text-3xl italic leading-relaxed" style={{
              color: '#f5e6d3'
            }} data-id="element-483">
                {t('launchPage.intro1')}
              </p>
              <p className="mt-4 text-base" style={{
              color: 'rgba(245, 230, 211, 0.6)'
            }} data-id="element-484">
                {t('launchPage.intro2')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Providers */}
        <section className="px-6 pb-16" data-id="element-485">
          <div className="max-w-5xl mx-auto space-y-6" data-id="element-486">
            {providers.map((p, i) => {
            const Icon = providerIcons[i] || Cloud;
            const isFeatured = i < 2;
            return <motion.article key={p.name} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.6,
              delay: i * 0.05
            }} className="rounded-2xl p-8 md:p-10 border backdrop-blur-sm" style={{
              backgroundColor: isFeatured ? 'rgba(60, 8, 16, 0.7)' : 'rgba(40, 8, 14, 0.5)',
              borderColor: isFeatured ? 'rgba(220, 38, 38, 0.4)' : 'rgba(245, 230, 211, 0.1)'
            }} data-id="element-487">
                  <div className="flex flex-col md:flex-row md:items-start gap-6" data-id="element-488">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{
                  backgroundColor: isFeatured ? 'rgba(220, 38, 38, 0.2)' : 'rgba(245, 230, 211, 0.06)'
                }} data-id="element-489">
                      <Icon className="w-6 h-6" style={{
                    color: isFeatured ? '#dc2626' : '#d4a574'
                  }} data-id="element-490" />
                    </div>

                    <div className="flex-1" data-id="element-491">
                      <div className="flex flex-wrap items-center gap-3 mb-3" data-id="element-492">
                        <h3 className="font-serif text-2xl md:text-3xl" style={{
                      color: '#f5e6d3'
                    }} data-id="element-493">
                          {p.name}
                        </h3>
                        <span className="text-xs uppercase tracking-widest px-3 py-1 rounded-full" style={{
                      backgroundColor: isFeatured ? 'rgba(220, 38, 38, 0.15)' : 'rgba(212, 165, 116, 0.1)',
                      color: isFeatured ? '#dc2626' : '#d4a574'
                    }} data-id="element-494">
                          {p.tag}
                        </span>
                      </div>

                      <p className="leading-relaxed mb-5" style={{
                    color: 'rgba(245, 230, 211, 0.75)'
                  }} data-id="element-495">
                        {p.desc}
                      </p>

                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-496">
                        {p.bullets.map((pt: string) => <li key={pt} className="flex items-center gap-2 text-sm" style={{
                      color: 'rgba(245, 230, 211, 0.65)'
                    }} data-id="element-497">
                            <span className="w-1.5 h-1.5 rounded-full" style={{
                        backgroundColor: isFeatured ? '#dc2626' : '#d4a574'
                      }} data-id="element-498" />
                            {pt}
                          </li>)}
                      </ul>
                    </div>
                  </div>
                </motion.article>;
          })}
          </div>
        </section>

        {/* Recommendation */}
        <section className="px-6 py-20" data-id="element-499">
          <div className="max-w-3xl mx-auto" data-id="element-500">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="rounded-3xl p-10 md:p-14 text-center border" style={{
            backgroundColor: 'rgba(60, 8, 16, 0.6)',
            borderColor: 'rgba(212, 165, 116, 0.3)'
          }} data-id="element-501">
              <Zap className="w-8 h-8 mx-auto mb-6" style={{
              color: '#d4a574'
            }} data-id="element-502" />
              <p className="text-xs uppercase tracking-[0.4em] mb-4" style={{
              color: '#d4a574'
            }} data-id="element-503">
                {t('launchPage.bottomTag')}
              </p>
              <p className="font-serif italic text-2xl md:text-3xl leading-snug mb-4" style={{
              color: '#f5e6d3'
            }} data-id="element-504">
                {t('launchPage.bottomTitleStart')}
                <span style={{
                color: '#dc2626'
              }} data-id="element-505">
                  {t('launchPage.bottomTitleHighlight')}
                </span>{' '}
                {t('launchPage.bottomTitleEnd')}
              </p>
              <p className="text-base leading-relaxed" style={{
              color: 'rgba(245, 230, 211, 0.65)'
            }} data-id="element-506">
                {t('launchPage.bottomDesc')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-6 py-10 border-t" style={{
        borderColor: 'rgba(245, 230, 211, 0.1)'
      }} data-id="element-507">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm" data-id="element-508">
            <Link to="/" className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity" style={{
            color: '#f5e6d3'
          }} data-id="element-509">
              <ArrowLeft className="w-4 h-4" data-id="element-510" />
              {t('common.backToMainStage')}
            </Link>
            <span style={{
            color: 'rgba(245, 230, 211, 0.5)'
          }} data-id="element-511">
              {t('launchPage.footerTag')}
            </span>
          </div>
        </footer>
      </div>
    </div>;
}